import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight, 
  ArrowLeftRight,
  Users, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  AlertCircle, 
  Share2, 
  RefreshCw,
  Plus,
  Minus,
  Search,
  Filter,
  Wallet,
  Shield,
  Image as ImageIcon,
  Box,
  Zap,
  Heart,
  X,
  ChevronRight,
  Package,
  Eye
} from 'lucide-react';
import PieceCard from '../components/PieceCard';
import ModelPreview from '../components/ModelPreview';
import PieceActivityAndHolders from '../components/PieceActivityAndHolders';
import VerifiedBadge from '../components/VerifiedBadge';
import LoadingScreen from '../components/LoadingScreen';
import Logo3D from '../components/Logo3D';
import { useWallet } from '../contexts/WalletContext';
import { useActivity } from '../contexts/ActivityContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useFavorites } from '../hooks/useFavorites';
import WalletRequired from '../components/WalletRequired';
import { 
  createTradeOnChain, 
  acceptTradeRequest,
  declineTradeRequest,
  cancelTradeRequest,
  confirmTradeOnChain, 
  executeAtomicTrade, 
  cancelTradeOnChain,
  isValidSolanaAddress,
  calculateExpirationTime,
  formatTimeRemaining,
  checkAndCancelExpiredTrades
} from '../utils/tradeContract';
import { tradeEvents } from '../utils/tradeEvents';
import { normalizePieces } from '../utils/pieceNormalizer.js';
import ModelViewer from '../components/ModelViewer.jsx';
import { getCategoryDisplayName } from '../utils/pieceUtils';
import { trackPieceView } from '../utils/supabaseViewTracking';
import { getCapsuleForPiece } from '../utils/supabaseDatabase';

const TradePieces = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { wallet, isConnected, publicKey, connectWallet, balance, usdPrice } = useWallet();
  const { logPieceTraded } = useActivity();
  const { solPrice } = useSolPrice();
  const { toggleFavorite, isFavorited } = useFavorites();
  
  // Trade states
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'incoming', 'requests', 'history'
  const [currentTrade, setCurrentTrade] = useState(null);
  const [trades, setTrades] = useState([]);
  const [tradeRequests, setTradeRequests] = useState([]);
  
  // Available pieces for trading (for other pages)
  const [availablePieces, setAvailablePieces] = useState([]);
  
  // Trade status
  const [isConfirmingTrade, setIsConfirmingTrade] = useState(false);
  const [isExecutingTrade, setIsExecutingTrade] = useState(null); // Now stores trade ID being executed
  const [isCancellingTrade, setIsCancellingTrade] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [cancelRequestId, setCancelRequestId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Trade execution progress modal
  const [tradeExecutionModal, setTradeExecutionModal] = useState({
    show: false,
    step: 0,
    totalSteps: 2,
    currentMessage: '',
    isComplete: false,
    transactionSignature: '',
    feePaid: 0,
    error: null
  });
  
  // Notification system
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm', type: 'warning' });
  
  // Piece detail modal
  const [pieceDetailModal, setPieceDetailModal] = useState({ isOpen: false, item: null });
  const [pieceCapsuleInfo, setPieceCapsuleInfo] = useState(null);
  const [modalViewMode, setModalViewMode] = useState('3d'); // '3d' or 'thumbnail'
  const [threeDStatus, setThreeDStatus] = useState('loading'); // 'loading', 'loaded', 'error'
  const [threeDError, setThreeDError] = useState(null);
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  // Helper function to show confirmation modal
  const showConfirm = (title, message, onConfirm, confirmText = 'Confirm', type = 'warning') => {
    setConfirmModal({ show: true, title, message, onConfirm, confirmText, type });
  };

  // Auto-refresh trades every 60 seconds to check for expired deadlines
  useEffect(() => {
    if (!isConnected || !publicKey) return;
    
    const refreshInterval = setInterval(() => {
      console.log('🔄 Auto-refreshing trades to check for expired deadlines...');
      loadTrades();
    }, 60000); // Refresh every 60 seconds

    return () => clearInterval(refreshInterval);
  }, [isConnected, publicKey]);

  // Helper function to calculate time remaining until expiration
  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return 'No expiration';
    
    const now = new Date();
    const expiration = new Date(expiresAt);
    const diffMs = expiration - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffDays > 0) {
      return `Expires in ${diffDays}d ${diffHours}h`;
    } else if (diffHours > 0) {
      return `Expires in ${diffHours}h ${diffMinutes}m`;
    } else {
      return `Expires in ${diffMinutes}m`;
    }
  };

  // Check for capsule information when piece detail modal opens
  useEffect(() => {
    if (!pieceDetailModal.isOpen || !pieceDetailModal.item?.id) {
      setPieceCapsuleInfo(null);
      return;
    }

    const checkCapsule = async () => {
      try {
        let itemId = pieceDetailModal.item.id;
        const itemIdString = String(itemId);

        if (itemIdString.startsWith('supabase_')) {
          itemId = itemIdString.replace('supabase_', '');
          if (!isNaN(itemId)) {
            itemId = parseInt(itemId, 10);
          }
        } else if (itemIdString.startsWith('local_')) {
          console.log('🔍 Local piece detected, skipping capsule check');
          setPieceCapsuleInfo(null);
          return;
        }

        console.log('🔍 [TRADE PIECES] Checking capsule for piece:', itemId);
        const capsuleInfo = await getCapsuleForPiece(itemId);
        console.log('🔍 [TRADE PIECES] Capsule info result:', capsuleInfo);

        setPieceCapsuleInfo(capsuleInfo);
      } catch (error) {
        console.error('❌ [TRADE PIECES] Error fetching capsule info:', error);
        setPieceCapsuleInfo(null);
      }
    };

    checkCapsule();
  }, [pieceDetailModal.isOpen, pieceDetailModal.item]);

  // Load user's pieces for trading
  useEffect(() => {
    const loadData = async () => {
      if (isConnected && publicKey) {
        setIsLoading(true);
        try {
          await Promise.all([
            loadUserPieces(),
            loadTrades(),
            loadTradeRequests()
          ]);
        } catch (error) {
          console.error('Error loading data:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
      
      // Check if we're joining an existing trade
      const tradeId = searchParams.get('trade');
      if (tradeId) {
        joinTrade(tradeId);
      }
    };
    
    loadData();
  }, [searchParams, isConnected, publicKey]);

  const loadUserPieces = async () => {
    if (!publicKey) {
      console.log('⚠️ No wallet connected');
      setAvailablePieces([]);
      return;
    }

    try {
      console.log('🔍 Loading pieces for wallet:', publicKey.toString());
      
      // Import supabase
      const { supabase } = await import('../lib/supabase');
      
      // Fetch minted pieces owned by this wallet from database
      const { data: pieces, error } = await supabase
        .from('pieces')
        .select(`
          *,
          social_links,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            verification_type,
            profile_picture_url,
            bio,
            social_links,
            total_pieces_created
          )
        `)
        .eq('holder_wallet', publicKey.toString())
        .not('mint_address', 'is', null);

      if (error) {
        console.error('Error fetching pieces:', error);
        setAvailablePieces([]);
        return;
      }

      console.log(`✅ Found ${pieces?.length || 0} minted pieces`);

      if (!pieces || pieces.length === 0) {
        console.log('ℹ️ No minted pieces found for this wallet');
        setAvailablePieces([]);
        return;
      }

      // Format pieces for display
      const formattedPieces = pieces.map(piece => ({
        id: piece.id,
        mint_address: piece.mint_address,
        name: piece.name || 'Unnamed Piece',
        // Use actual purchase price (last_sale_price_lamports) first, then fall back to listing/base price
        price: piece.last_sale_price_lamports 
          ? piece.last_sale_price_lamports / 1_000_000_000 
          : (piece.listing_price || piece.price || '0'),
        category: piece.category || 'Other',
        color: piece.color || 'Unknown',
        modelUrl: piece.model_url || piece.preview_glb_url,
        imageUrl: piece.preview_image_url || piece.thumbnail_url || piece.image_url,
        status: 'minted', // All pieces in this query are minted
        listing_status: piece.listing_status,
        rarity: piece.rarity || `#${Math.floor(Math.random() * 10000) + 1}`,
        views: piece.view_count || 0,
        likes: piece.like_count || 0,
        createdAt: piece.created_at
      }));

      setAvailablePieces(formattedPieces);
    } catch (error) {
      console.error('Error loading user pieces:', error);
      setAvailablePieces([]);
    }
  };

  // Helper function to enrich piece data from database
  const enrichPieceData = async (pieces) => {
    if (!pieces || pieces.length === 0) return [];
    
    try {
      const { supabase } = await import('../lib/supabase');
      
      // Get all mint addresses
      const mintAddresses = pieces.map(p => p.mint_address).filter(Boolean);
      
      if (mintAddresses.length === 0) return pieces;
      
      // Fetch full piece data from database with creator info
      const { data: fullPieces, error } = await supabase
        .from('pieces')
        .select(`
          *,
          social_links,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            verification_type,
            profile_picture_url,
            bio,
            social_links,
            total_pieces_created
          )
        `)
        .in('mint_address', mintAddresses);
      
      if (error) {
        console.error('Error enriching piece data:', error);
        return pieces;
      }
      
      // Get unique holder wallet addresses to fetch their profiles
      const holderWallets = [...new Set(
        fullPieces?.map(p => p.holder_wallet).filter(Boolean) || []
      )];
      
      // Fetch holder profile data from creators table
      let holderProfiles = new Map();
      if (holderWallets.length > 0) {
        const { data: holders, error: holderError } = await supabase
          .from('creators')
          .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, total_pieces_created')
          .in('wallet', holderWallets);
        
        if (!holderError && holders) {
          holders.forEach(holder => {
            if (holder.wallet) {
              holderProfiles.set(holder.wallet, holder);
            }
          });
        }
      }
      
      // Create a map of mint_address -> full piece data
      const pieceMap = new Map();
      fullPieces?.forEach(piece => {
        if (piece.mint_address) {
          pieceMap.set(piece.mint_address, piece);
        }
      });
      
      // Enrich the pieces with full data
      return pieces.map(piece => {
        const fullPiece = pieceMap.get(piece.mint_address);
        if (fullPiece) {
          // Map creator data for compatibility with modal
          const creator = fullPiece.creators ? {
            wallet: fullPiece.creators.wallet,
            username: fullPiece.creators.handle,
            handle: fullPiece.creators.handle,
            verified: fullPiece.creators.is_verified,
            profile_picture_url: fullPiece.creators.profile_picture_url,
            piecesCount: fullPiece.creators.total_pieces_created
          } : null;
          
          // Map holder data for compatibility with modal
          const holderProfile = holderProfiles.get(fullPiece.holder_wallet);
          const holder = fullPiece.holder_wallet ? {
            wallet: fullPiece.holder_wallet,
            handle: holderProfile?.handle || 'Anonymous',
            username: holderProfile?.handle || 'Anonymous',
            verified: holderProfile?.is_verified || false,
            profile_picture_url: holderProfile?.profile_picture_url || null,
            piecesCount: holderProfile?.total_pieces_created || 0
          } : null;
          
          return {
            ...piece,
            ...fullPiece,
            // Keep trade-specific fields
            mint_address: piece.mint_address,
            imageUrl: fullPiece.preview_image_url || fullPiece.thumbnail_url || fullPiece.image_url || piece.image_url,
            // Use actual purchase price (last_sale_price_lamports) first, then fall back to listing/base price
            price: fullPiece.last_sale_price_lamports 
              ? fullPiece.last_sale_price_lamports / 1_000_000_000 
              : (fullPiece.listing_price || fullPiece.price || 0),
            // Add creator info for modal compatibility
            creator: creator,
            originalCreator: creator,
            creator_wallet: fullPiece.creator_wallet,
            // Add holder info with full profile data
            holder_wallet: fullPiece.holder_wallet,
            holder: holder
          };
        }
        return piece;
      });
    } catch (error) {
      console.error('Error enriching pieces:', error);
      return pieces;
    }
  };

  const loadTrades = async () => {
    if (!publicKey) {
      console.log('⚠️ No wallet connected, cannot load trades');
      setTrades([]);
      setTradeRequests([]);
      return;
    }

    try {
      console.log('🔍 Loading trades from database for wallet:', publicKey.toString());
      
      // First, check and auto-cancel any expired confirmed trades
      const cancelResult = await checkAndCancelExpiredTrades();
      if (cancelResult.success && cancelResult.cancelled_count > 0) {
        console.log(`⚠️ Auto-cancelled ${cancelResult.cancelled_count} expired trade(s)`);
        showNotification(
          `${cancelResult.cancelled_count} confirmed trade(s) expired and were automatically cancelled`,
          'info'
        );
      }
      
      // Import trade functions
      const { getWalletTrades } = await import('../utils/tradeContract');
      
      // Get all trades for this wallet
      const result = await getWalletTrades(publicKey.toString());
      
      if (!result.success) {
        console.error('Failed to load trades:', result.error);
        setTrades([]);
        return;
      }

      console.log(`✅ Loaded ${result.trades.length} trades from database`);
      console.log('📦 Raw trades data:', result.trades);
      
      // Format trades for display and enrich piece data
      const formattedTrades = await Promise.all(result.trades.map(async (trade) => {
        // Map database status to frontend status
        let mappedStatus = trade.status;
        if (trade.status === 'both_confirmed') {
          mappedStatus = 'ready_to_execute';
        } else if (trade.status === 'executed') {
          mappedStatus = 'completed';
        }
        
        // Enrich piece data with full database info
        const enrichedInitiatorPieces = await enrichPieceData(trade.initiator_pieces || []);
        const enrichedCounterpartyPieces = await enrichPieceData(trade.counterparty_pieces || []);
        
        // Calculate time remaining for execution deadline
        // Show deadline for counterparty_confirmed (waiting for initiator) or both_confirmed (ready to execute)
        const hasDeadline = (trade.status === 'counterparty_confirmed' || trade.status === 'both_confirmed') && trade.confirmed_expires_at;
        const deadlineTime = hasDeadline ? trade.confirmed_expires_at : trade.expires_at;
        const timeLeftText = formatTimeRemaining(deadlineTime);
        
        // Debug logging for deadline
        if (trade.status === 'counterparty_confirmed' || trade.status === 'both_confirmed') {
          console.log('🔍 Trade deadline check:', {
            id: trade.id.slice(-8),
            status: trade.status,
            confirmed_expires_at: trade.confirmed_expires_at,
            hasDeadline,
            timeLeftText
          });
        }
        
        return {
          ...trade,
          // Map wallet addresses
          initiator: trade.initiator_wallet,
          counterparty: trade.counterparty_wallet,
          // Map pieces with enriched data
          initiatorPieces: enrichedInitiatorPieces,
          counterpartyPieces: enrichedCounterpartyPieces,
          // Map confirmations
          initiatorConfirmed: trade.initiator_confirmed,
          counterpartyConfirmed: trade.counterparty_confirmed,
          // Map status
          status: mappedStatus,
          // Map other fields
          tradeLink: trade.trade_link || `${window.location.origin}/trade-pieces?trade=${trade.id}`,
          createdAt: trade.created_at,
          expiresAt: trade.expires_at,
          confirmedExpiresAt: trade.confirmed_expires_at,
          deadlineTime: deadlineTime,
          timeLeft: timeLeftText,
          hasExecutionDeadline: hasDeadline,
          message: trade.message || ''
        };
      }));

      console.log('✨ Formatted trades with enriched pieces:', formattedTrades);
      setTrades(formattedTrades);
      
    } catch (error) {
      console.error('Error loading trades:', error);
      setTrades([]);
    }
  };

  const loadTradeRequests = async () => {
    if (!publicKey) {
      console.log('⚠️ No wallet connected, cannot load trade requests');
      setTradeRequests([]);
      return;
    }

    try {
      console.log('🔍 Loading trade requests from database for wallet:', publicKey.toString());
      
      // Import supabase
      const { supabase } = await import('../lib/supabase');
      
      // First, update any expired trade requests
      const now = new Date().toISOString();
      const { data: expiredRequests, error: expiredError } = await supabase
        .from('trade_requests')
        .update({ status: 'expired' })
        .eq('status', 'pending')
        .lt('expires_at', now)
        .or(`requester_wallet.eq.${publicKey.toString()},target_wallet.eq.${publicKey.toString()}`)
        .select();
      
      if (expiredError) {
        console.error('Error updating expired requests:', expiredError);
      } else if (expiredRequests && expiredRequests.length > 0) {
        console.log(`⏰ Marked ${expiredRequests.length} trade requests as expired`);
      }
      
      // Get trade requests where user is requester OR target
      const { data: requests, error } = await supabase
        .from('trade_requests')
        .select('*')
        .or(`requester_wallet.eq.${publicKey.toString()},target_wallet.eq.${publicKey.toString()}`)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Failed to load trade requests:', error);
        setTradeRequests([]);
        return;
      }

      console.log(`✅ Loaded ${requests?.length || 0} trade requests`);
      
      // Format and enrich trade requests
      const formattedRequests = await Promise.all((requests || []).map(async (request) => {
        // Enrich piece data
        const enrichedOffered = await enrichPieceData(request.offered_pieces || []);
        const enrichedRequested = await enrichPieceData(request.requested_pieces || []);
        
        // If accepted, get associated trade status
        let associatedTradeStatus = null;
        if (request.status === 'accepted' && request.trade_id) {
          const { data: trade } = await supabase
            .from('p2p_trades')
            .select('status')
            .eq('id', request.trade_id)
            .maybeSingle();
          
          if (trade) {
            associatedTradeStatus = trade.status;
          }
        }
        
        return {
          ...request,
          id: request.id,
          requester: request.requester_wallet,
          target: request.target_wallet,
          offeredPieces: enrichedOffered,
          requestedPieces: enrichedRequested,
          status: request.status,
          message: request.message || '',
          createdAt: request.created_at,
          expiresAt: request.expires_at,
          tradeId: request.trade_id,
          associatedTradeStatus: associatedTradeStatus,
          isIncoming: request.target_wallet === publicKey.toString(),
          isOutgoing: request.requester_wallet === publicKey.toString()
        };
      }));

      console.log('✨ Formatted trade requests:', formattedRequests);
      setTradeRequests(formattedRequests);
      
    } catch (error) {
      console.error('Error loading trade requests:', error);
      setTradeRequests([]);
    }
  };

  const handleAcceptTradeRequest = (requestId) => {
    if (!isConnected) {
      showNotification('Please connect your wallet to accept this trade request', 'error');
      connectWallet();
      return;
    }

    showConfirm(
      'Accept Trade Request',
      'This will set your pieces as approved and notify the initiator to complete the trade. Once both parties confirm, you will have 3 days to execute the trade before it automatically expires.',
      async () => {
        setConfirmModal({ ...confirmModal, show: false });
        setIsConfirmingTrade(true);

        try {
          console.log('✅ Accepting trade request:', requestId);
          
          const result = await acceptTradeRequest(wallet, requestId);
          
          if (!result.success) {
            throw new Error(result.error || 'Failed to accept trade request');
          }

          showNotification(`Trade accepted! Waiting for the counterparty to execute the trade.`, 'success');
      
          // Reload both trade requests and trades
          await loadTradeRequests();
          await loadTrades();
          
          // Stay on incoming tab - the counterparty doesn't need to do anything else
          // The initiator will see it in their Execute tab
          
        } catch (error) {
          console.error('Error accepting trade request:', error);
          showNotification(`Failed to accept trade request: ${error.message}`, 'error');
        } finally {
          setIsConfirmingTrade(false);
        }
      },
      'Accept Request',
      'success'
    );
  };

  const handleDeclineTradeRequest = async (requestId) => {
    if (!isConnected) {
      showNotification('Please connect your wallet to decline this trade request', 'error');
      connectWallet();
      return;
    }

    showConfirm(
      'Decline Trade Request',
      'Are you sure you want to decline this trade request?',
      async () => {
        setConfirmModal({ ...confirmModal, show: false });
        try {
          console.log('❌ Declining trade request:', requestId);
          
          const result = await declineTradeRequest(wallet, requestId, '');
          
          if (!result.success) {
            throw new Error(result.error || 'Failed to decline trade request');
          }

          showNotification('Trade request declined.', 'success');
          
          // Reload trade requests
          await loadTradeRequests();
          
        } catch (error) {
          console.error('Error declining trade request:', error);
          showNotification(`Failed to decline trade request: ${error.message}`, 'error');
        }
      },
      'Decline',
      'danger'
    );
  };

  const handleCancelTradeRequest = (requestId) => {
    if (!isConnected) {
      showNotification('Please connect your wallet to cancel this trade request', 'error');
      connectWallet();
      return;
    }

    // Show confirmation modal
    setCancelRequestId(requestId);
    setShowCancelConfirmation(true);
  };

  const confirmCancelTradeRequest = async () => {
    if (!cancelRequestId) return;

    try {
      console.log('❌ Cancelling trade request:', cancelRequestId);
      
      const result = await cancelTradeRequest(wallet, cancelRequestId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel trade request');
      }

      showNotification('Trade request cancelled.', 'success');
      
      // Reload trade requests
      await loadTradeRequests();
      
    } catch (error) {
      console.error('Error cancelling trade request:', error);
      showNotification(`Failed to cancel trade request: ${error.message}`, 'error');
    } finally {
      setShowCancelConfirmation(false);
      setCancelRequestId(null);
    }
  };

  const joinTrade = (tradeId) => {
    const trade = trades.find(t => t.id === tradeId);
    if (trade) {
      setCurrentTrade(trade);
      setActiveTab('pending');
    }
  };

  const confirmTrade = async (tradeId = null) => {
    if (!isConnected) {
      showNotification('Please connect your wallet to confirm the trade', 'error');
      connectWallet();
      return;
    }

    // Get the trade ID
    const targetTradeId = tradeId || currentTrade?.id;
    if (!targetTradeId) {
      showNotification('No trade selected to confirm', 'error');
      return;
    }

    // Check if user has enough SOL for the transaction fee
    const tradeFee = 0.01; // 0.01 SOL fee
    if (balance < tradeFee) {
      showNotification(`Insufficient SOL balance. You need at least ${tradeFee} SOL to confirm this trade, but you only have ${balance.toFixed(4)} SOL.`, 'error');
      return;
    }

    setIsConfirmingTrade(true);

    try {
      // Confirm trade on blockchain
      const onChainResult = await confirmTradeOnChain(wallet, targetTradeId);
      
      if (!onChainResult.success) {
        throw new Error(onChainResult.error || 'Failed to confirm trade on blockchain');
      }
      
      // Reload trades from database to get updated status
      await loadTrades();
      
      // Check if trade is now ready to execute
      const updatedTrade = onChainResult.trade;
      if (updatedTrade && updatedTrade.status === 'both_confirmed') {
        showNotification('✅ Trade confirmed! Both parties ready. Click "Execute Trade" to complete and pay 0.05 SOL.', 'success');
      } else if (updatedTrade && updatedTrade.initiator_confirmed && updatedTrade.counterparty_confirmed) {
        showNotification('✅ Trade confirmed! Both parties ready. Click "Execute Trade" to complete and pay 0.05 SOL.', 'success');
      } else {
        // This shouldn't happen with the new flow, but just in case
        showNotification('✅ Trade confirmed! Counterparty is already ready. Click "Execute Trade" to complete and pay 0.05 SOL.', 'success');
      }

    } catch (error) {
      console.error('Error confirming trade:', error);
      showNotification(`Failed to confirm trade: ${error.message}`, 'error');
    } finally {
      setIsConfirmingTrade(false);
    }
  };

  const cancelTrade = async (tradeId = null) => {
    if (!isConnected) {
      showNotification('Please connect your wallet to cancel the trade', 'error');
      connectWallet();
      return;
    }

    const targetTradeId = tradeId || currentTrade?.id;
    if (!targetTradeId) {
      showNotification('No trade selected to cancel', 'error');
      return;
    }

    setIsCancellingTrade(true);

    try {
      // Cancel trade on blockchain
      const onChainResult = await cancelTradeOnChain(wallet, targetTradeId);
      
      if (!onChainResult.success) {
        throw new Error(onChainResult.error || 'Failed to cancel trade on blockchain');
      }

      // Reload trades from database
      await loadTrades();
      
      if (currentTrade?.id === targetTradeId) {
        setCurrentTrade(null);
      }

      showNotification('Trade cancelled successfully', 'success');

    } catch (error) {
      console.error('Error cancelling trade:', error);
      showNotification(`Failed to cancel trade: ${error.message}`, 'error');
    } finally {
      setIsCancellingTrade(false);
    }
  };

  const executeTrade = async (tradeId = null) => {
    if (!isConnected) {
      showNotification('Please connect your wallet to execute the trade', 'error');
      connectWallet();
      return;
    }

    // Get the trade to execute
    const targetTradeId = tradeId || currentTrade?.id;
    const trade = trades.find(t => t.id === targetTradeId);
    
    if (!trade) {
      showNotification('Trade not found', 'error');
      return;
    }

    // Check if user has enough SOL for the transaction fee
    const tradeFee = 0.1; // 0.05 SOL fee + buffer for gas
    if (balance < tradeFee) {
      showNotification(`Insufficient SOL balance. You need at least ${tradeFee} SOL to execute this trade, but you only have ${balance.toFixed(4)} SOL.`, 'error');
      return;
    }

    setIsExecutingTrade(targetTradeId);
    
    // Show progress modal
    setTradeExecutionModal({
      show: true,
      step: 0,
      totalSteps: 2,
      currentMessage: 'Initializing trade execution...',
      isComplete: false,
      transactionSignature: '',
      feePaid: 0,
      error: null
    });

    try {
      console.log('🚀 Starting trade execution...', trade.id);
      
      const isInitiator = trade.initiator === publicKey?.toString();
      
      // Step 1: If initiator hasn't confirmed yet, confirm first (set delegates)
      if (isInitiator && !trade.initiatorConfirmed) {
        console.log('⚙️ Step 1: Setting delegate approvals for your pieces...');
        setTradeExecutionModal(prev => ({
          ...prev,
          step: 1,
          currentMessage: 'Setting delegate approvals for your pieces...'
        }));
        
        const confirmResult = await confirmTradeOnChain(wallet, trade.id);
        
        if (!confirmResult.success) {
          throw new Error(confirmResult.error || 'Failed to set delegate approvals');
        }
        
        console.log('✅ Delegate approvals set!');
        
        // Reload trades to get updated status
        await loadTrades();
      }
      
      // Step 2: Execute atomic trade on blockchain (pays the 0.05 SOL fee for initiator)
      console.log('⚙️ Step 2: Executing trade and paying 0.05 SOL fee...');
      setTradeExecutionModal(prev => ({
        ...prev,
        step: 2,
        currentMessage: 'Executing atomic swap on blockchain...'
      }));
      
      const executeResult = await executeAtomicTrade(wallet, trade.id);
      
      if (!executeResult.success) {
        throw new Error(executeResult.error || 'Failed to execute trade');
      }

      console.log('✅ Trade execution result:', executeResult);

      // Reload trades from database
      await loadTrades();
      
      // Reload available pieces to reflect ownership changes
      await loadUserPieces();
      
      // Emit trade event so other pages can refresh
      tradeEvents.emit({
        type: 'trade_executed',
        tradeId: trade.id,
        initiator: trade.initiator,
        counterparty: trade.counterparty,
        timestamp: new Date().toISOString()
      });
      
      setCurrentTrade(null);

      // Show success modal
      setTradeExecutionModal({
        show: true,
        step: 2,
        totalSteps: 2,
        currentMessage: 'Trade completed successfully!',
        isComplete: true,
        transactionSignature: executeResult.signature || '',
        feePaid: executeResult.feePaid || 0.05,
        error: null
      });

      // Switch to history tab after modal is closed
      setTimeout(() => {
        setActiveTab('history');
      }, 500);

    } catch (error) {
      console.error('❌ Error executing trade:', error);
      setTradeExecutionModal(prev => ({
        ...prev,
        error: error.message,
        isComplete: false
      }));
      
      // Auto-hide error after 3 seconds
      setTimeout(() => {
        setTradeExecutionModal(prev => ({ ...prev, show: false }));
        showNotification(`Failed to execute trade: ${error.message}`, 'error');
      }, 3000);
    } finally {
      setIsExecutingTrade(null);
    }
  };

  const acceptTrade = async (tradeId) => {
    if (!isConnected) {
      showNotification('Please connect your wallet to accept the trade', 'error');
      connectWallet();
      return;
    }

    // Find the trade
    const trade = trades.find(t => t.id === tradeId);
    if (!trade) {
      showNotification('Trade not found', 'error');
      return;
    }

    // Check if user has enough SOL for the transaction fee
    const tradeFee = 0.01; // 0.01 SOL fee
    if (balance < tradeFee) {
      showNotification(`Insufficient SOL balance. You need at least ${tradeFee} SOL to accept this trade, but you only have ${balance.toFixed(4)} SOL.`, 'error');
      return;
    }

    setIsConfirmingTrade(true);

    try {
      // Set as current trade for UI
      setCurrentTrade(trade);

      // Step 1: Confirm your side of the trade (set delegate approvals for your pieces)
      console.log('🎯 Accepting trade - setting delegate approvals...');
      const confirmResult = await confirmTradeOnChain(wallet, tradeId);
      
      if (!confirmResult.success) {
        throw new Error(confirmResult.error || 'Failed to set delegate approvals');
      }

      console.log('✅ Delegate approvals set, now executing atomic swap...');

      // Step 2: Execute the atomic swap immediately
      const executeResult = await executeAtomicTrade(
        wallet, 
        trade.id,
        trade.initiator,
        trade.counterparty,
        trade.initiatorPieces.map(p => p.mint_address || p.mintAddress || p.id),
        trade.counterpartyPieces.map(p => p.mint_address || p.mintAddress || p.id)
      );
      
      if (!executeResult.success) {
        throw new Error(executeResult.error || 'Failed to execute trade');
      }

      // Reload trades from database
      await loadTrades();
      
      // Reload available pieces to reflect ownership changes
      await loadUserPieces();
      
      // Emit trade event so other pages can refresh
      tradeEvents.emit({
        type: 'trade_accepted',
        tradeId: trade.id,
        initiator: trade.initiator,
        counterparty: trade.counterparty,
        timestamp: new Date().toISOString()
      });
      
      setCurrentTrade(null);

      showNotification('Trade accepted and executed successfully! NFTs have been exchanged.', 'success');
      setActiveTab('history'); // Switch to history tab

    } catch (error) {
      console.error('Error accepting trade:', error);
      showNotification(`Failed to accept trade: ${error.message}`, 'error');
    } finally {
      setIsConfirmingTrade(false);
    }
  };

  const denyTrade = async (tradeId) => {
    if (!isConnected) {
      showNotification('Please connect your wallet to deny the trade', 'error');
      connectWallet();
      return;
    }

    try {
      const updatedTrades = trades.map(trade => {
        if (trade.id === tradeId) {
          return { ...trade, status: 'denied', deniedAt: new Date().toISOString() };
        }
        return trade;
      });

      localStorage.setItem('trades', JSON.stringify(updatedTrades));
      setTrades(updatedTrades);

      // Remove the trade request if it exists
      const updatedRequests = tradeRequests.filter(r => r.id !== tradeId);
      localStorage.setItem('tradeRequests', JSON.stringify(updatedRequests));
      setTradeRequests(updatedRequests);

      showNotification('Trade denied', 'success');

    } catch (error) {
      console.error('Error denying trade:', error);
      showNotification(`Failed to deny trade: ${error.message}`, 'error');
    }
  };

  const copyTradeLink = () => {
    if (currentTrade?.tradeLink) {
      navigator.clipboard.writeText(currentTrade.tradeLink);
      showNotification('Trade link copied to clipboard!', 'success');
    }
  };

  const shareTrade = () => {
    if (currentTrade?.tradeLink) {
      if (navigator.share) {
        navigator.share({
          title: 'Trade Pieces on Outsera',
          text: `Check out this piece trade: ${currentTrade.initiatorPieces.map(p => p.name).join(', ')}`,
          url: currentTrade.tradeLink
        });
      } else {
        copyTradeLink();
      }
    }
  };

  // Handle piece detail modal
  const openPieceDetailModal = async (item) => {
    console.log('🔍 openPieceDetailModal: Opening modal for item:', item);
    console.log('📦 Full piece object:', JSON.stringify(item, null, 2));
    
    // Normalize the piece to ensure proper field mapping
    const normalizedPiece = item ? normalizePieces([item])[0] : item;
    console.log('🔍 Normalized piece:', normalizedPiece);
    
    setPieceDetailModal({ isOpen: true, item: normalizedPiece });
    
    // Track view using Supabase Edge Function
    if (item?.id) {
      const walletAddress = publicKey?.toString() || null;
      // Convert piece ID to string to ensure compatibility
      const pieceIdString = String(item.id);
      const result = await trackPieceView(pieceIdString, walletAddress);
      if (result.success) {
        console.log('✅ View tracked via Supabase:', result.stats);
      } else {
        console.log('⏭️ View not tracked:', result.error);
      }
    }
    
    // Check if piece has 3D model and set initial view mode
    const modelBucket = item?.preview_bucket || item?.model_bucket;
    const modelKey = item?.preview_key || item?.model_key;
    const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
    const has3DModel = (modelBucket && modelKey) || (fallbackSrc && (fallbackSrc.includes('.glb') || fallbackSrc.includes('.gltf')));
    
    console.log('🔍 Modal opening - has3DModel:', has3DModel, 'modelBucket:', modelBucket, 'modelKey:', modelKey, 'fallbackSrc:', fallbackSrc);
    
    if (has3DModel) {
      setModalViewMode('3d');
      setThreeDStatus('loading');
      setThreeDError(null);
    } else {
      setModalViewMode('thumbnail');
      setThreeDStatus('error');
      setThreeDError('No 3D model available');
    }
  };

  const closePieceDetailModal = () => {
    setPieceDetailModal({ isOpen: false, item: null });
    setActivityPanelOpen(false);
  };

  // Handle account button click - navigate to creator profile
  const handleAccountClick = (item) => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
    }
  };

  // Handle holder button click - navigate to holder profile
  const handleHolderClick = (item) => {
    const holderWallet = item?.holder?.wallet || item?.holder_wallet;
    if (holderWallet) {
      navigate(`/creator/${holderWallet}`);
    }
  };


  const getTradeStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-400';
      case 'confirmed': return 'text-blue-400';
      case 'ready_to_execute': return 'text-purple-400';
      case 'completed': return 'text-green-400';
      case 'cancelled': return 'text-red-400';
      case 'expired': return 'text-gray-400';
      default: return 'text-gray-400';
    }
  };

  const getTradeStatusIcon = (status) => {
    switch (status) {
      case 'pending': return Clock;
      case 'confirmed': return CheckCircle;
      case 'ready_to_execute': return Zap;
      case 'completed': return CheckCircle;
      case 'cancelled': return XCircle;
      case 'expired': return XCircle;
      default: return Clock;
    }
  };

  // Helper function to create File from data URL
  // (Moved outside component for performance)

  // Use the utility function from tradeContract.js
  // const formatTimeRemaining = formatTimeRemaining;

  // Filter trades based on active tab (for p2p_trades)
  const filteredTrades = trades.filter(trade => {
    const isInitiator = trade.initiator === publicKey?.toString();
    const isCounterparty = trade.counterparty === publicKey?.toString();
    
    switch (activeTab) {
      case 'pending':
        // Execute Trades tab - Only show for initiator (Wallet 1)
        // Wallet 2 accepts the trade, auto-confirms, and doesn't need to see it here
        // Wallet 1 must confirm and then execute
        // Exclude expired trades (they go to history)
        return isInitiator && (trade.status === 'pending' || trade.status === 'counterparty_confirmed' || trade.status === 'both_confirmed') && trade.status !== 'expired';
      case 'history':
        // Completed, cancelled, or expired trades
        return trade.status === 'completed' || trade.status === 'cancelled' || trade.status === 'executed' || trade.status === 'expired';
      default:
        return false;
    }
  });

  // Filter trade requests based on active tab (for trade_requests)
  const filteredTradeRequests = tradeRequests.filter(request => {
    switch (activeTab) {
      case 'incoming':
        // Incoming trade requests where user is target
        // Show pending requests OR accepted requests with active associated trades
        if (!request.isIncoming) return false;
        
        if (request.status === 'pending') return true;
        
        if (request.status === 'accepted' && request.associatedTradeStatus) {
          // Keep showing if trade is still active (not completed/cancelled/expired)
          const activeTradeStatuses = ['pending', 'initiator_confirmed', 'counterparty_confirmed', 'both_confirmed'];
          return activeTradeStatuses.includes(request.associatedTradeStatus);
        }
        
        return false;
        
      case 'requests':
        // Outgoing trade requests where user is requester
        // Only show pending requests - once accepted, they move to Execute Trades tab
        return request.isOutgoing && request.status === 'pending';
        
      case 'history':
        // Show expired, declined, cancelled trade requests
        // OR accepted requests where the associated trade is completed/cancelled/expired
        if (request.status === 'expired' || request.status === 'declined' || request.status === 'cancelled') {
          return true;
        }
        
        if (request.status === 'accepted' && request.associatedTradeStatus) {
          // Show in history if trade is completed/cancelled/expired
          const completedTradeStatuses = ['executed', 'cancelled', 'expired'];
          return completedTradeStatuses.includes(request.associatedTradeStatus);
        }
        
        // Also show accepted requests without an associated trade (shouldn't happen but just in case)
        if (request.status === 'accepted' && !request.associatedTradeStatus) {
          return true;
        }
        
        return false;
        
      default:
        return false;
    }
  });

  return (
    <WalletRequired 
      message="You need to connect your wallet to access trading features."
      action="trade pieces"
    >
      <div className="min-h-screen bg-black">
        {/* Header */}
        <div className="bg-black">
          <div className="px-6 py-6">
            <h1 className="text-3xl font-bold text-white mb-2">Trade Pieces</h1>
            <p className="text-gray-400">Exchange pieces with other collectors</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-[#2a2a2a] bg-black">
          <div className="px-6">
            <div className="flex gap-1">
              {[
                { id: 'pending', label: 'Execute Trades', icon: Zap },
                { id: 'incoming', label: 'Incoming Trades', icon: Users },
                { id: 'requests', label: 'Trade Requests', icon: ArrowLeftRight },
                { id: 'history', label: 'Trade History', icon: RefreshCw }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'text-white border-b-2 border-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Loading State */}
          {isLoading ? (
            <LoadingScreen message="Loading trades" fullScreen={false} />
          ) : (
            <>
          {activeTab === 'pending' && (
            <div className="space-y-6">
              {/* Fee Notice */}
              <div className="bg-gray-500/10 rounded-xl p-4 border border-gray-500/30">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">ℹ</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-1">Execute Your Trades</h4>
                    <p className="text-gray-300 text-sm">
                      After someone accepts your trade, click <span className="font-semibold">"Execute Trade"</span> to complete the swap. 
                      You'll pay a <span className="font-semibold text-gray-400">0.05 SOL fee</span> and the pieces will be exchanged instantly.
                    </p>
                  </div>
                </div>
              </div>
              
              {filteredTrades.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-[#2a2a2a] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Zap size={40} className="text-gray-400" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-400 mb-2">No trades ready to execute</h3>
                  <p className="text-gray-500">After someone accepts your trade, it will appear here for you to confirm and execute</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTrades.map(trade => {
                    const isInitiator = trade.initiator === publicKey?.toString();
                    const isCounterparty = trade.counterparty === publicKey?.toString();
                    const StatusIcon = getTradeStatusIcon(trade.status);
                    
                    return (
                      <div key={trade.id} className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-[#2a2a2a] rounded-full flex items-center justify-center">
                              <Users size={24} className="text-gray-400" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">
                                Trade #{trade.id.slice(-8)}
                              </h3>
                              <div className="flex items-center gap-2 text-sm text-gray-400">
                                <Zap size={16} className="text-green-400" />
                                <span className="text-green-400">Ready to Execute (Counterparty Accepted)</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Execution Deadline Timer */}
                          {trade.hasExecutionDeadline && trade.timeLeft && (
                            <div className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium backdrop-blur-md shadow-sm border ${
                              trade.timeLeft.includes('Expired') || trade.timeLeft.includes('hour') || trade.timeLeft.includes('minute')
                                ? 'bg-red-500/90 text-white border-red-400/50 animate-pulse'
                                : trade.timeLeft.includes('1 day') || trade.timeLeft.includes('0 day')
                                ? 'bg-orange-500/90 text-white border-orange-400/50'
                                : 'bg-blue-500/90 text-white border-blue-400/50'
                            }`}>
                              <Clock size={12} />
                              <span>Execute in: {trade.timeLeft}</span>
                            </div>
                          )}
                        </div>

                        {/* Trade Details */}
                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                          {/* Your Pieces / Their Pieces */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-3">
                              {isInitiator ? 'Your Pieces' : 'Their Pieces'} ({trade.initiatorPieces.length})
                            </h4>
                            <div className="space-y-2">
                              {trade.initiatorPieces.map((piece, idx) => (
                                <button 
                                  key={idx} 
                                  onClick={() => openPieceDetailModal(piece)}
                                  className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors group cursor-pointer"
                                >
                                  {piece.imageUrl && (
                                    <img 
                                      src={piece.imageUrl} 
                                      alt={piece.name}
                                      className="w-10 h-10 object-cover rounded-md"
                                    />
                                  )}
                                  <div className="text-white text-sm group-hover:text-gray-300 transition-colors">{piece.name}</div>
                                  <div className="text-green-400 text-sm ml-auto">{piece.price} SOL</div>
                                  <Eye size={16} className="text-gray-400 group-hover:text-gray-300 transition-colors" />
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-3">
                              {isCounterparty ? 'Your Pieces' : 'Their Pieces'} ({trade.counterpartyPieces.length})
                            </h4>
                            <div className="space-y-2">
                              {trade.counterpartyPieces.map((piece, idx) => (
                                <button 
                                  key={idx} 
                                  onClick={() => openPieceDetailModal(piece)}
                                  className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors group cursor-pointer"
                                >
                                  {piece.imageUrl && (
                                    <img 
                                      src={piece.imageUrl} 
                                      alt={piece.name}
                                      className="w-10 h-10 object-cover rounded-md"
                                    />
                                  )}
                                  <div className="text-white text-sm group-hover:text-gray-300 transition-colors">{piece.name}</div>
                                  <div className="text-green-400 text-sm ml-auto">{piece.price} SOL</div>
                                  <Eye size={16} className="text-gray-400 group-hover:text-gray-300 transition-colors" />
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Message */}
                        {trade.message && (
                          <div className="bg-[#0a0a0a] rounded-lg p-4 mb-6">
                            <h4 className="text-sm font-medium text-gray-300 mb-2">Trade Message</h4>
                            <p className="text-gray-400 text-sm">{trade.message}</p>
                          </div>
                        )}

                        {/* Trade Status */}
                        <div className="bg-green-500/10 rounded-lg p-4 mb-6 border border-green-500/30">
                          <div className="flex items-center gap-2">
                            <CheckCircle size={20} className="text-green-400" />
                            <div>
                              <h4 className="text-sm font-medium text-green-400">Counterparty Accepted</h4>
                              <p className="text-sm text-gray-400 mt-1">Click "Execute Trade" below to complete the swap and pay 0.05 SOL fee</p>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                          {/* Single Execute button that does everything */}
                          <button
                            onClick={() => executeTrade(trade.id)}
                            disabled={isExecutingTrade === trade.id}
                            className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {isExecutingTrade === trade.id ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Executing...
                              </>
                            ) : (
                              <>
                                <Zap size={16} />
                                Execute Trade (0.05 SOL)
                              </>
                            )}
                          </button>
                          
                          <button
                            onClick={() => cancelTrade(trade.id)}
                            disabled={isCancellingTrade}
                            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {isCancellingTrade ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Cancelling...
                              </>
                            ) : (
                              <>
                                <XCircle size={16} />
                                Cancel Trade
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'incoming' && (
            <div className="space-y-6">
              {filteredTradeRequests.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-[#2a2a2a] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Users size={40} className="text-gray-400" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-400 mb-2">No incoming trade requests</h3>
                  <p className="text-gray-500">Trade requests from other collectors will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTradeRequests.map(request => {
                    return (
                      <div key={request.id} className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-[#2a2a2a] rounded-full flex items-center justify-center">
                              <Users size={24} className="text-gray-400" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">
                                Trade Request #{request.id.slice(-8)}
                              </h3>
                              <div className="flex items-center gap-2 text-sm text-gray-400">
                                {request.status === 'pending' ? (
                                  <>
                                    <Clock size={16} className="text-orange-400" />
                                    <span className="text-orange-400">Pending Your Response</span>
                                  </>
                                ) : request.status === 'accepted' ? (
                                  <>
                                    <CheckCircle size={16} className="text-green-400" />
                                    <span className="text-green-400">Accepted - Waiting for Initiator to Execute</span>
                                  </>
                                ) : null}
                                <span>•</span>
                                <span>
                                  From: <button 
                                    onClick={() => navigate(`/creator/${request.requester}`)}
                                    className="text-blue-400 hover:text-blue-300 hover:underline transition-colors cursor-pointer"
                                  >
                                    {request.requester.slice(0, 6)}...{request.requester.slice(-4)}
                                  </button>
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-400 mt-1">
                                <Clock size={14} />
                                <span>{getTimeRemaining(request.expiresAt)}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Trade Details */}
                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                          <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-3">
                              They're Offering ({request.offeredPieces.length})
                            </h4>
                            <div className="space-y-2">
                              {request.offeredPieces.map((piece, idx) => (
                                <button 
                                  key={idx} 
                                  onClick={() => openPieceDetailModal(piece)}
                                  className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors group cursor-pointer"
                                >
                                  {piece.imageUrl && (
                                    <img 
                                      src={piece.imageUrl} 
                                      alt={piece.name}
                                      className="w-10 h-10 object-cover rounded-md"
                                    />
                                  )}
                                  <div className="text-white text-sm group-hover:text-gray-300 transition-colors">{piece.name}</div>
                                  <div className="text-green-400 text-sm ml-auto">{piece.price} SOL</div>
                                  <Eye size={16} className="text-gray-400 group-hover:text-gray-300 transition-colors" />
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-3">
                              They Want From You ({request.requestedPieces.length})
                            </h4>
                            <div className="space-y-2">
                              {request.requestedPieces.map((piece, idx) => (
                                <button 
                                  key={idx} 
                                  onClick={() => openPieceDetailModal(piece)}
                                  className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors group cursor-pointer"
                                >
                                  {piece.imageUrl && (
                                    <img 
                                      src={piece.imageUrl} 
                                      alt={piece.name}
                                      className="w-10 h-10 object-cover rounded-md"
                                    />
                                  )}
                                  <div className="text-white text-sm group-hover:text-gray-300 transition-colors">{piece.name}</div>
                                  <div className="text-green-400 text-sm ml-auto">{piece.price} SOL</div>
                                  <Eye size={16} className="text-gray-400 group-hover:text-gray-300 transition-colors" />
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Message */}
                        {request.message && (
                          <div className="bg-[#0a0a0a] rounded-lg p-4 mb-6">
                            <h4 className="text-sm font-medium text-gray-300 mb-2">Message</h4>
                            <p className="text-gray-400 text-sm">{request.message}</p>
                          </div>
                        )}

                        {/* Action Buttons - Only show for pending requests */}
                        {request.status === 'pending' ? (
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleAcceptTradeRequest(request.id)}
                              disabled={isConfirmingTrade}
                              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {isConfirmingTrade ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Accepting...
                                </>
                              ) : (
                                <>
                                  <CheckCircle size={16} />
                                  Accept Request
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleDeclineTradeRequest(request.id)}
                              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                            >
                              <XCircle size={16} />
                              Decline
                            </button>
                          </div>
                        ) : request.status === 'accepted' ? (
                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                            <p className="text-blue-400 text-sm font-medium flex items-center gap-2">
                              <CheckCircle size={16} />
                              You've accepted this trade. The initiator will execute the trade soon.
                            </p>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="space-y-6">
              {filteredTradeRequests.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-[#2a2a2a] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <ArrowLeftRight size={40} className="text-gray-400" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-400 mb-2">No pending trade requests</h3>
                  <p className="text-gray-500">Trade requests you've sent to other collectors will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTradeRequests.map(request => {
                    return (
                      <div key={request.id} className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-[#2a2a2a] rounded-full flex items-center justify-center">
                              <ArrowLeftRight size={24} className="text-gray-400" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">
                                Trade Request #{request.id.slice(-8)}
                              </h3>
                              <div className="flex items-center gap-2 text-sm text-gray-400">
                                <Clock size={16} className="text-gray-400" />
                                <span className="text-gray-400">
                                  Pending Acceptance
                                </span>
                                <span>•</span>
                                <span>
                                  To: <button 
                                    onClick={() => navigate(`/creator/${request.target}`)}
                                    className="text-blue-400 hover:text-blue-300 hover:underline transition-colors cursor-pointer"
                                  >
                                    {request.target.slice(0, 6)}...{request.target.slice(-4)}
                                  </button>
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-400 mt-1">
                                <Clock size={14} />
                                <span>{getTimeRemaining(request.expiresAt)}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Trade Details */}
                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                          <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-3">
                              You're Offering ({request.offeredPieces.length})
                            </h4>
                            <div className="space-y-2">
                              {request.offeredPieces.map((piece, idx) => (
                                <button 
                                  key={idx} 
                                  onClick={() => openPieceDetailModal(piece)}
                                  className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors group cursor-pointer"
                                >
                                  {piece.imageUrl && (
                                    <img 
                                      src={piece.imageUrl} 
                                      alt={piece.name}
                                      className="w-10 h-10 object-cover rounded-md"
                                    />
                                  )}
                                  <div className="text-white text-sm group-hover:text-gray-300 transition-colors">{piece.name}</div>
                                  <div className="text-green-400 text-sm ml-auto">{piece.price} SOL</div>
                                  <Eye size={16} className="text-gray-400 group-hover:text-gray-300 transition-colors" />
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-3">
                              You're Requesting ({request.requestedPieces.length})
                            </h4>
                            <div className="space-y-2">
                              {request.requestedPieces.map((piece, idx) => (
                                <button 
                                  key={idx} 
                                  onClick={() => openPieceDetailModal(piece)}
                                  className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors group cursor-pointer"
                                >
                                  {piece.imageUrl && (
                                    <img 
                                      src={piece.imageUrl} 
                                      alt={piece.name}
                                      className="w-10 h-10 object-cover rounded-md"
                                    />
                                  )}
                                  <div className="text-white text-sm group-hover:text-gray-300 transition-colors">{piece.name}</div>
                                  <div className="text-green-400 text-sm ml-auto">{piece.price} SOL</div>
                                  <Eye size={16} className="text-gray-400 group-hover:text-gray-300 transition-colors" />
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Message */}
                        {request.message && (
                          <div className="bg-[#0a0a0a] rounded-lg p-4 mb-6">
                            <h4 className="text-sm font-medium text-gray-300 mb-2">Your Message</h4>
                            <p className="text-gray-400 text-sm">{request.message}</p>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleCancelTradeRequest(request.id)}
                            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                          >
                            <XCircle size={16} />
                            Cancel Request
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              {filteredTrades.length === 0 && filteredTradeRequests.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-[#2a2a2a] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <RefreshCw size={40} className="text-gray-400" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-400 mb-2">No trade history</h3>
                  <p className="text-gray-500">Your completed, cancelled, and expired trades will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTrades.map(trade => {
                    const isInitiator = trade.initiator === publicKey?.toString();
                    const StatusIcon = getTradeStatusIcon(trade.status);
                    
                    return (
                      <div key={trade.id} className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-[#2a2a2a] rounded-full flex items-center justify-center">
                              <StatusIcon size={24} className={getTradeStatusColor(trade.status)} />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">
                                Trade #{trade.id.slice(-8)}
                              </h3>
                              <div className="flex items-center gap-2 text-sm text-gray-400">
                                <span className={getTradeStatusColor(trade.status)}>
                                  {trade.status.charAt(0).toUpperCase() + trade.status.slice(1)}
                                </span>
                                <span>•</span>
                                <span>
                                  {new Date(trade.createdAt || trade.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Trade Summary */}
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-3">
                              {isInitiator ? 'Your Pieces' : 'Their Pieces'} ({trade.initiatorPieces.length})
                            </h4>
                            <div className="space-y-2">
                              {trade.initiatorPieces.map((piece, idx) => (
                                <button 
                                  key={idx} 
                                  onClick={() => openPieceDetailModal(piece)}
                                  className="w-full flex items-center gap-3 p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors group cursor-pointer"
                                >
                                  {piece.imageUrl && (
                                    <img 
                                      src={piece.imageUrl} 
                                      alt={piece.name}
                                      className="w-8 h-8 object-cover rounded-md"
                                    />
                                  )}
                                  <span className="text-white text-sm group-hover:text-gray-300 transition-colors">{piece.name}</span>
                                  <Eye size={14} className="text-gray-400 group-hover:text-gray-300 transition-colors ml-auto" />
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-3">
                              {isInitiator ? 'Their Pieces' : 'Your Pieces'} ({trade.counterpartyPieces.length})
                            </h4>
                            <div className="space-y-2">
                              {trade.counterpartyPieces.map((piece, idx) => (
                                <button 
                                  key={idx} 
                                  onClick={() => openPieceDetailModal(piece)}
                                  className="w-full flex items-center gap-3 p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors group cursor-pointer"
                                >
                                  {piece.imageUrl && (
                                    <img 
                                      src={piece.imageUrl} 
                                      alt={piece.name}
                                      className="w-8 h-8 object-cover rounded-md"
                                    />
                                  )}
                                  <span className="text-white text-sm group-hover:text-gray-300 transition-colors">{piece.name}</span>
                                  <Eye size={14} className="text-gray-400 group-hover:text-gray-300 transition-colors ml-auto" />
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Trade Requests in History */}
                  {filteredTradeRequests.map(request => {
                    const isRequester = request.requester === publicKey?.toString();
                    const getRequestStatusColor = (status) => {
                      switch (status) {
                        case 'expired': return 'text-orange-400';
                        case 'declined': return 'text-red-400';
                        case 'cancelled': return 'text-gray-400';
                        case 'accepted': return 'text-green-400';
                        default: return 'text-gray-400';
                      }
                    };
                    
                    return (
                      <div key={`request-${request.id}`} className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-[#2a2a2a] rounded-full flex items-center justify-center">
                              <ArrowLeftRight size={24} className="text-gray-400" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">
                                Trade Request #{request.id.slice(-8)}
                              </h3>
                              <div className="flex items-center gap-2 text-sm text-gray-400">
                                <span className={getRequestStatusColor(request.status)}>
                                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                </span>
                                <span>•</span>
                                <span>
                                  {new Date(request.createdAt || request.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Trade Request Summary */}
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-3">
                              {isRequester ? 'Your Pieces' : 'Their Pieces'} ({request.offeredPieces?.length || 0})
                            </h4>
                            <div className="space-y-2">
                              {(request.offeredPieces || []).map((piece, idx) => (
                                <button 
                                  key={idx} 
                                  onClick={() => openPieceDetailModal(piece)}
                                  className="w-full flex items-center gap-3 p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors group cursor-pointer"
                                >
                                  {piece.imageUrl && (
                                    <img 
                                      src={piece.imageUrl} 
                                      alt={piece.name}
                                      className="w-8 h-8 object-cover rounded-md"
                                    />
                                  )}
                                  <span className="text-white text-sm group-hover:text-gray-300 transition-colors">{piece.name}</span>
                                  <Eye size={14} className="text-gray-400 group-hover:text-gray-300 transition-colors ml-auto" />
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-sm font-medium text-gray-300 mb-3">
                              {isRequester ? 'Their Pieces' : 'Your Pieces'} ({request.requestedPieces?.length || 0})
                            </h4>
                            <div className="space-y-2">
                              {(request.requestedPieces || []).map((piece, idx) => (
                                <button 
                                  key={idx} 
                                  onClick={() => openPieceDetailModal(piece)}
                                  className="w-full flex items-center gap-3 p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors group cursor-pointer"
                                >
                                  {piece.imageUrl && (
                                    <img 
                                      src={piece.imageUrl} 
                                      alt={piece.name}
                                      className="w-8 h-8 object-cover rounded-md"
                                    />
                                  )}
                                  <span className="text-white text-sm group-hover:text-gray-300 transition-colors">{piece.name}</span>
                                  <Eye size={14} className="text-gray-400 group-hover:text-gray-300 transition-colors ml-auto" />
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
            </>
          )}
        </div>

        {/* Piece Detail Modal */}
        {pieceDetailModal.isOpen && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={closePieceDetailModal}
          >
            <div 
              className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
              onClick={(e) => e.stopPropagation()}
              style={{ overflow: 'visible' }}
            >
              <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
                {/* Left side - Thumbnail/3D Model */}
                <div className="w-1/2 p-6 overflow-y-auto">
                  {/* 3D Model Toggle Buttons */}
                  {(() => {
                    const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                    const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
                    
                    if (has3DModel) {
                      return (
                        <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                          <button
                            onClick={() => setModalViewMode('3d')}
                            className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                              modalViewMode === '3d'
                                ? 'text-white bg-[#2a2a2a] shadow-lg' 
                                : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                            }`}
                          >
                            3D Model
                          </button>
                          <button
                            onClick={() => setModalViewMode('thumbnail')}
                            className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                              modalViewMode === 'thumbnail'
                                ? 'text-white bg-[#2a2a2a] shadow-lg' 
                                : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                            }`}
                          >
                            Thumbnail
                          </button>
                        </div>
                      );
                    }
                    return null;
                  })()}
                
                  <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square">
                    {(() => {
                      const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                      
                      if (modalViewMode === '3d') {
                        // Try to get bucket/key info from the piece data
                        const modelBucket = pieceDetailModal.item?.preview_bucket || pieceDetailModal.item?.model_bucket;
                        const modelKey = pieceDetailModal.item?.preview_key || pieceDetailModal.item?.model_key;
                        
                        // Fallback to legacy URL if bucket/key not available
                        const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                        
                        if (modelBucket && modelKey) {
                          const customOrientation = pieceDetailModal.item?.customOrientation || null;
                          
                          return (
                            <ModelViewer
                              modelBucket={modelBucket}
                              modelKey={modelKey}
                              posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                              orientation={customOrientation}
                              className="w-full h-full modal"
                              onLoad={() => {
                                console.log('✅ Modal: 3D model loaded successfully');
                                setThreeDStatus('loaded');
                              }}
                              onError={(error) => {
                                console.error('❌ Modal: 3D model error:', error);
                                setThreeDStatus('error');
                                setThreeDError(error.message || 'Failed to load 3D model');
                              }}
                            />
                          );
                        } else if (fallbackSrc) {
                          const customOrientation = pieceDetailModal.item?.customOrientation || null;
                          
                          return (
                            <ModelViewer
                              src={fallbackSrc}
                              posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                              orientation={customOrientation}
                              className="w-full h-full modal"
                              onLoad={() => {
                                console.log('✅ Modal: 3D model loaded successfully');
                                setThreeDStatus('loaded');
                              }}
                              onError={(error) => {
                                console.error('❌ Modal: 3D model error:', error);
                                setThreeDStatus('error');
                                setThreeDError(error.message || 'Failed to load 3D model');
                              }}
                            />
                          );
                        } else {
                          return (
                            <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                              <div className="text-center text-white p-6">
                                <div className="text-6xl mb-4">⚠️</div>
                                <div className="text-xl font-semibold mb-2">No 3D model available</div>
                                <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                              </div>
                            </div>
                          );
                        }
                      } else {
                        // Show thumbnail image - check multiple possible field names
                        const thumbnailUrl = pieceDetailModal.item?.imageUrl || 
                                            pieceDetailModal.item?.preview_image_url || 
                                            pieceDetailModal.item?.thumbnail_url || 
                                            pieceDetailModal.item?.image_url ||
                                            pieceDetailModal.item?.thumbnailUrl;
                        
                        console.log('🖼️ Thumbnail view - available URLs:', {
                          imageUrl: pieceDetailModal.item?.imageUrl,
                          preview_image_url: pieceDetailModal.item?.preview_image_url,
                          thumbnail_url: pieceDetailModal.item?.thumbnail_url,
                          image_url: pieceDetailModal.item?.image_url,
                          thumbnailUrl: pieceDetailModal.item?.thumbnailUrl,
                          selected: thumbnailUrl
                        });
                        
                        return thumbnailUrl ? (
                          <img 
                            src={thumbnailUrl} 
                            alt={pieceDetailModal.item.name}
                            className="w-full h-full object-cover rounded-xl"
                            onError={(e) => {
                              console.log('❌ Modal thumbnail load error for:', pieceDetailModal.item.name, 'URL:', thumbnailUrl);
                              e.target.src = '/images/piece-placeholder.png';
                              e.target.onerror = null;
                            }}
                          />
                        ) : pieceDetailModal.item?.thumbnail?.dataUrl ? (
                          <img 
                            src={pieceDetailModal.item.thumbnail.dataUrl} 
                            alt={pieceDetailModal.item.name}
                            className="w-full h-full object-cover rounded-xl"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                            <div className="text-center text-white p-6">
                              <div className="text-6xl mb-4">🖼️</div>
                              <div className="text-xl font-semibold mb-2">No preview available</div>
                              <div className="text-sm text-gray-400">This piece doesn't have a preview image</div>
                            </div>
                          </div>
                        );
                      }
                    })()}
                  </div>

                  {/* Creator and Holder Section */}
                  <div className="space-y-4 mb-6">
                    {/* Original Creator */}
                    <div>
                      <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                        Original Creator
                      </div>
                      <button 
                        onClick={() => handleAccountClick(pieceDetailModal.item)}
                        className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                      >
                        <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                          {(pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url) ? (
                            <img
                              src={pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url}
                              alt={pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator'}
                              className="w-full h-full object-cover rounded-full"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {(pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator').charAt(0)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="flex items-center gap-1.5">
                            <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                              {pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Original Creator'}
                            </div>
                            {(pieceDetailModal.item?.originalCreator?.verified || pieceDetailModal.item?.creator?.verified) && (
                              <VerifiedBadge 
                                size="sm"
                                verificationType={pieceDetailModal.item?.originalCreator?.verificationType || pieceDetailModal.item?.creator?.verificationType || 'none'}
                              />
                            )}
                          </div>
                          <div className="text-gray-400 text-xs mt-0.5">
                            {pieceDetailModal.item?.originalCreator?.piecesCount || pieceDetailModal.item?.creator?.piecesCount || '0'} pieces created
                          </div>
                        </div>
                        <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6"/>
                          </svg>
                        </div>
                      </button>

                      {/* Creator Royalty - Outside button */}
                      {pieceDetailModal.item?.royalty_bps !== undefined && pieceDetailModal.item?.royalty_bps !== null && (
                        <div className="mt-3 px-1">
                          <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                            {(pieceDetailModal.item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                          </span>
                        </div>
                      )}

                      {/* Social Links */}
                      {(() => {
                        // Prioritize capsule social links if piece belongs to a capsule, otherwise use piece social links
                        const socialLinks = pieceCapsuleInfo
                          ? {
                              website: pieceCapsuleInfo.website,
                              twitter: pieceCapsuleInfo.twitter,
                              instagram: pieceCapsuleInfo.instagram,
                              discord: pieceCapsuleInfo.discord
                            }
                          : (pieceDetailModal.item?.social_links || {});
                        const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                        
                        // DEBUG: Log social links data
                        console.log('🔍 [TRADE PIECES] Social Links Debug:', {
                          pieceId: pieceDetailModal.item?.id,
                          pieceName: pieceDetailModal.item?.name,
                          pieceCapsuleInfo,
                          social_links: socialLinks,
                          hasSocialLinks
                        });
                        
                        if (!hasSocialLinks) return null;
                        
                        return (
                          <div className="flex items-center gap-2 mt-3 px-1">
                            {socialLinks.website && (
                              <a
                                href={socialLinks.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                                title="Website"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                  <circle cx="12" cy="12" r="10"/>
                                  <line x1="2" y1="12" x2="22" y2="12"/>
                                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                </svg>
                              </a>
                            )}
                            {socialLinks.twitter && (
                              <a
                                href={socialLinks.twitter}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                                title="X (Twitter)"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                              </a>
                            )}
                            {socialLinks.instagram && (
                              <a
                                href={socialLinks.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                                title="Instagram"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                                </svg>
                              </a>
                            )}
                            {socialLinks.discord && (
                              <a
                                href={socialLinks.discord}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                                title="Discord"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                                </svg>
                              </a>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Right side - Details */}
                <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-2xl font-bold text-white">
                          {pieceDetailModal.item?.name}
                        </h2>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(pieceDetailModal.item.id);
                          }}
                          className="p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                          title={isFavorited(pieceDetailModal.item.id) ? "Remove from favorites" : "Add to favorites"}
                        >
                          <Heart 
                            size={20} 
                            className={`transition-all duration-300 ${
                              isFavorited(pieceDetailModal.item.id)
                                ? 'text-red-500 fill-red-500'
                                : 'text-gray-400 hover:text-red-500'
                            }`} 
                          />
                        </button>
                      </div>
                      {/* Custom Tags */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {pieceDetailModal.item?.tags && pieceDetailModal.item.tags.length > 0 ? (
                          pieceDetailModal.item.tags.map((tag, index) => (
                            <span 
                              key={index}
                              className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">No tags</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                        activityPanelOpen 
                          ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                          : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                      }`}
                    >
                      Activity
                    </button>
                  </div>

                  <div className="space-y-5">
                    {/* Category and Color */}
                    <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                          <span className="text-gray-300 text-sm font-medium">
                            {getCategoryDisplayName(pieceDetailModal.item?.category) || 'Uncategorized'}
                          </span>
                        </div>
                        {pieceDetailModal.item?.color && (
                          <div className="text-gray-300 text-sm">
                            <span className="text-gray-500">Color: </span>
                            <span className="capitalize font-medium">{pieceDetailModal.item.color}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                      <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        {pieceDetailModal.item?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                      <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">Price</span>
                          <div className="text-green-400 font-bold text-xl">
                            {(() => {
                              const item = pieceDetailModal.item;
                              const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                              return displayPrice;
                            })()} SOL
                          </div>
                        </div>
                        <div className="text-right text-gray-500 text-sm">
                          ≈ ${(() => {
                            const item = pieceDetailModal.item;
                            const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                            return ((displayPrice || 0) * (solPrice || 150)).toFixed(2);
                          })()} USD
                        </div>
                      </div>
                    </div>

                    {/* Scarcity */}
                    <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                      <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                      <div className="text-white font-semibold text-lg">
                        {pieceDetailModal.item?.quantity || 1}
                      </div>
                    </div>

                    {/* Dropped Date */}
                    <div className="text-gray-500 text-sm pt-2">
                      Dropped {pieceDetailModal.item?.createdAt ? new Date(pieceDetailModal.item.createdAt).toLocaleDateString('en-US', { 
                        year: '2-digit', 
                        month: '2-digit', 
                        day: '2-digit' 
                      }) : 'Recently'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Side Panel - slides out from behind modal on desktop, overlay on mobile */}
              <div 
                className="absolute top-0 left-0 md:left-full h-full w-full md:w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] md:rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
                style={{ 
                  transform: activityPanelOpen ? 'translateX(0)' : (window.innerWidth < 768 ? 'translateX(-100%)' : 'translateX(-100%)'),
                  zIndex: activityPanelOpen ? 50 : 0,
                  opacity: activityPanelOpen ? 1 : 0,
                  visibility: activityPanelOpen ? 'visible' : 'hidden',
                  pointerEvents: activityPanelOpen ? 'auto' : 'none'
                }}
              >
                <div className="h-full flex flex-col p-5">
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                    <h3 className="text-base font-bold text-white">Activity</h3>
                    <button
                      onClick={() => setActivityPanelOpen(false)}
                      className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                      title="Close Activity Panel"
                    >
                      <ChevronRight size={18} className="text-gray-400" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <PieceActivityAndHolders
                      pieceId={pieceDetailModal.item?.id}
                      pieceName={pieceDetailModal.item?.name}
                      basePrice={parseFloat(pieceDetailModal.item?.price) || 0}
                      solPrice={solPrice || 150}
                      totalQuantity={pieceDetailModal.item?.total_quantity || pieceDetailModal.item?.quantity || 1}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Request Confirmation Modal */}
        {showCancelConfirmation && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4">
            <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-md w-full">
              {/* Header */}
              <div className="p-6 border-b border-[#2a2a2a]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#2a2a2a] rounded-full flex items-center justify-center">
                    <XCircle size={24} className="text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Cancel Trade Request</h3>
                    <p className="text-gray-400 text-sm">This action cannot be undone</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-gray-300 text-sm">
                  Are you sure you want to cancel this trade request? The other party will be notified and you'll need to create a new request if you change your mind.
                </p>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-[#2a2a2a]">
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowCancelConfirmation(false);
                      setCancelRequestId(null);
                    }}
                    className="flex-1 px-4 py-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-xl transition-colors font-medium"
                  >
                    Keep Request
                  </button>
                  <button
                    onClick={confirmCancelTradeRequest}
                    className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <XCircle size={16} />
                    Cancel Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trade Execution Progress Modal */}
        {tradeExecutionModal.show && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
            <div className="bg-[#111111] border-2 border-gray-600/50 rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl shadow-gray-500/10">
              {!tradeExecutionModal.error && !tradeExecutionModal.isComplete ? (
                <>
                  {/* Loading State */}
                  <div className="p-8">
                    <div className="flex flex-col items-center">
                      {/* Animated 3D Logo */}
                      <div className="relative w-24 h-24 mb-6">
                        <Logo3D 
                          className="w-full h-full"
                          autoRotate={true}
                          baseRotationSpeed={0.03}
                          enableControls={false}
                          showEnvironment={true}
                          cameraPosition={[0, 0, 3.5]}
                          fov={50}
                          loadingMode={true}
                        />
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-2xl font-bold text-white mb-2">Executing Trade</h3>
                      <p className="text-gray-400 text-center mb-6">{tradeExecutionModal.currentMessage}</p>
                      
                      {/* Progress Steps */}
                      <div className="w-full max-w-md space-y-3 mb-6">
                        {[
                          { step: 1, label: 'Setting Delegate Approvals' },
                          { step: 2, label: 'Executing Atomic Swap' }
                        ].map(({ step, label }) => (
                          <div key={step} className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                              tradeExecutionModal.step > step 
                                ? 'bg-green-500/20 border-2 border-green-500' 
                                : tradeExecutionModal.step === step 
                                ? 'bg-gradient-to-r from-gray-300/20 to-gray-100/20 border-2 border-gray-300'
                                : 'bg-[#2a2a2a] border-2 border-[#2a2a2a]'
                            }`}>
                              {tradeExecutionModal.step > step ? (
                                <CheckCircle size={16} className="text-green-400" />
                              ) : tradeExecutionModal.step === step ? (
                                <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-400 rounded-full animate-pulse"></div>
                              ) : (
                                <span className="text-gray-600 text-xs font-bold">{step}</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className={`text-sm font-medium transition-colors ${
                                tradeExecutionModal.step >= step ? 'text-white' : 'text-gray-600'
                              }`}>
                                {label}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Loading Bar */}
                      <div className="w-full bg-[#2a2a2a] rounded-full h-2 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-gray-300 via-gray-100 to-gray-400 rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${(tradeExecutionModal.step / tradeExecutionModal.totalSteps) * 100}%` }}
                        ></div>
                      </div>
                      
                      {/* Info Text */}
                      <p className="text-gray-500 text-xs mt-4 text-center">
                        Please don't close this window. This may take a few moments...
                      </p>
                    </div>
                  </div>
                </>
              ) : tradeExecutionModal.isComplete ? (
                <>
                  {/* Success State */}
                  <div className="p-8">
                    <div className="flex flex-col items-center">
                      {/* Success Icon */}
                      <div className="relative w-24 h-24 mb-6">
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-300 via-gray-100 to-gray-400 rounded-full opacity-30"></div>
                        <div className="absolute inset-2 bg-[#111111] rounded-full flex items-center justify-center">
                          <CheckCircle size={48} className="text-green-400" />
                        </div>
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-2xl font-bold text-white mb-2">Trade Complete! 🎉</h3>
                      <p className="text-gray-400 text-center mb-6">
                        Your NFTs have been successfully exchanged
                      </p>
                      
                      {/* Transaction Details */}
                      <div className="w-full bg-[#0a0a0a] rounded-xl p-4 mb-6 space-y-3">
                        {tradeExecutionModal.feePaid > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Transaction Fee</span>
                            <span className="text-white font-medium">{tradeExecutionModal.feePaid} SOL</span>
                          </div>
                        )}
                        
                        {tradeExecutionModal.transactionSignature && (
                          <div className="flex items-start justify-between gap-3">
                            <span className="text-gray-400 text-sm">Transaction</span>
                            <a
                              href={`https://solscan.io/tx/${tradeExecutionModal.transactionSignature}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-300 hover:text-white text-sm font-mono truncate max-w-[200px] transition-colors"
                            >
                              {tradeExecutionModal.transactionSignature.substring(0, 8)}...
                            </a>
                          </div>
                        )}
                      </div>
                      
                      {/* Action Button */}
                      <button
                        onClick={() => setTradeExecutionModal({ ...tradeExecutionModal, show: false })}
                        className="w-full px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl font-medium transition-colors"
                      >
                        View Trade History
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Error State */}
                  <div className="p-8">
                    <div className="flex flex-col items-center">
                      {/* Error Icon */}
                      <div className="relative w-24 h-24 mb-6">
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full opacity-20"></div>
                        <div className="absolute inset-2 bg-[#111111] rounded-full flex items-center justify-center">
                          <XCircle size={48} className="text-red-400" />
                        </div>
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-2xl font-bold text-white mb-2">Trade Failed</h3>
                      <p className="text-gray-400 text-center mb-6">
                        {tradeExecutionModal.error || 'An error occurred while executing the trade'}
                      </p>
                      
                      {/* Action Button */}
                      <button
                        onClick={() => setTradeExecutionModal({ ...tradeExecutionModal, show: false })}
                        className="w-full px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl font-medium transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Notification Toast */}
        {notification.show && (
          <div className="fixed top-4 right-4 z-[70] animate-slide-down">
            <div className={`${
              notification.type === 'success' ? 'bg-green-500/20 border-green-500/50' :
              notification.type === 'error' ? 'bg-red-500/20 border-red-500/50' :
              'bg-blue-500/20 border-blue-500/50'
            } border rounded-xl p-4 shadow-xl max-w-md`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  notification.type === 'success' ? 'bg-green-500/20' :
                  notification.type === 'error' ? 'bg-red-500/20' :
                  'bg-blue-500/20'
                }`}>
                  {notification.type === 'success' ? (
                    <CheckCircle size={20} className="text-green-400" />
                  ) : notification.type === 'error' ? (
                    <AlertTriangle size={20} className="text-red-400" />
                  ) : (
                    <AlertCircle size={20} className="text-blue-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm">{notification.message}</p>
                </div>
                <button
                  onClick={() => setNotification({ ...notification, show: false })}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Generic Confirmation Modal */}
        {confirmModal.show && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4">
            <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-md w-full">
              {/* Header */}
              <div className="p-6 border-b border-[#2a2a2a]">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    confirmModal.type === 'success' ? 'bg-green-500/20' :
                    confirmModal.type === 'danger' ? 'bg-red-500/20' :
                    'bg-yellow-500/20'
                  }`}>
                    {confirmModal.type === 'success' ? (
                      <CheckCircle size={24} className="text-green-400" />
                    ) : confirmModal.type === 'danger' ? (
                      <XCircle size={24} className="text-red-400" />
                    ) : (
                      <AlertCircle size={24} className="text-yellow-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{confirmModal.title}</h3>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-gray-300 text-sm">{confirmModal.message}</p>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-[#2a2a2a]">
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                    className="flex-1 px-4 py-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-xl transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmModal.onConfirm}
                    className={`flex-1 px-4 py-3 text-white rounded-xl transition-colors font-medium ${
                      confirmModal.type === 'success' ? 'bg-green-600 hover:bg-green-700' :
                      confirmModal.type === 'danger' ? 'bg-red-600 hover:bg-red-700' :
                      'bg-yellow-600 hover:bg-yellow-700'
                    }`}
                  >
                    {confirmModal.confirmText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </WalletRequired>
  );
};

// Helper function to create file from data URL
const createFileFromDataUrl = (dataUrl, name, type) => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], name, { type: type || mime });
};

export default TradePieces;
